import { storeBackendBaseUrl } from '../core/backend-url-store.js';
import type { ChildDevice, ChildProfile, InternetAccessAccount, PairingCode } from './types.js';

/**
 * The SPA's only network layer — the browser-side counterpart of the BFF
 * (`src/server/`).
 *
 * Note what is *absent* here compared to the Android app's network
 * package: no auth interceptor, no token authenticator, no token storage.
 * The browser holds no backend credential at all — it sends its
 * `HttpOnly` session cookie, and the BFF attaches the parent token
 * server-side (see `src/server/sessions.ts` for why). What remains on this
 * side is the CSRF token, which is the one secret the browser *must* be
 * able to read, and which is therefore kept in memory only, never in
 * `localStorage`.
 */

/** Thrown for any non-2xx response, carrying text safe to show the parent. */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }

  /** True when the session is gone and the SPA must fall back to the login screen. */
  get isUnauthenticated(): boolean {
    return this.status === 401;
  }
}

/**
 * In-memory only, deliberately — see this file's doc comment. A reload
 * clears it and `restoreSession()` fetches a freshly rotated one.
 */
let csrfToken: string | null = null;

/**
 * Best-effort extraction of Nest's `{ statusCode, message, error }` body —
 * the port of Android's `HttpErrorMessage.backendMessageOrNull`. The
 * backend's `message` is text it deliberately chose to say (the
 * Internet-Access `ConflictException` reasons, for instance), so it is
 * safe to surface; anything unparsable falls back to the caller's own
 * generic wording rather than leaking a raw error string.
 */
async function backendMessageOrNull(response: Response): Promise<string | null> {
  try {
    const body: unknown = await response.json();
    if (typeof body === 'object' && body !== null) {
      const { message } = body as { message?: unknown };
      if (typeof message === 'string') return message;
      // Nest's ValidationPipe returns an array of messages.
      if (Array.isArray(message) && typeof message[0] === 'string') return message[0];
    }
  } catch {
    // Not JSON, or empty — caller supplies the fallback.
  }
  return null;
}

async function request<T>(
  method: string,
  path: string,
  body?: unknown,
  fallbackMessage = 'Die Anfrage ist fehlgeschlagen',
): Promise<T> {
  const headers: Record<string, string> = { Accept: 'application/json' };
  if (body !== undefined) headers['Content-Type'] = 'application/json';
  if (method !== 'GET' && csrfToken) headers['X-CSRF-Token'] = csrfToken;

  const response = await fetch(path, {
    method,
    headers,
    body: body === undefined ? undefined : JSON.stringify(body),
    // The session cookie is same-origin; never widen this.
    credentials: 'same-origin',
  });

  if (!response.ok) {
    if (response.status === 401) csrfToken = null;
    throw new ApiError(response.status, (await backendMessageOrNull(response)) ?? fallbackMessage);
  }

  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

// -- Session ---------------------------------------------------------------

export interface SessionStatus {
  authenticated: boolean;
  /**
   * What to prefill the login form's server field with: the live
   * session's own API for an authenticated visit, the deployment default
   * otherwise. A value remembered in `localStorage` takes precedence over
   * this — see `LoginPage`.
   */
  backendBaseUrl: string;
}

/**
 * Called once on page load. Says whether a live BFF session already
 * exists — a reload does not cost a re-login, because the session never
 * lived in the browser in the first place.
 */
export async function restoreSession(): Promise<SessionStatus> {
  const result = await request<{
    authenticated: boolean;
    csrfToken?: string;
    backendBaseUrl: string;
  }>('GET', '/session');
  csrfToken = result.csrfToken ?? null;
  // Keep the remembered value in step with the session that is actually
  // live, so a reload can never show one API while talking to another.
  if (result.authenticated) storeBackendBaseUrl(result.backendBaseUrl);
  return { authenticated: result.authenticated, backendBaseUrl: result.backendBaseUrl };
}

/**
 * `backendBaseUrl` is the one moment the API target can be chosen; the
 * BFF freezes it into the session and every later call uses that copy.
 * An empty value means "the deployment's default".
 */
export async function login(
  email: string,
  password: string,
  backendBaseUrl: string,
): Promise<void> {
  const result = await request<{ csrfToken: string; backendBaseUrl: string }>(
    'POST',
    '/session/login',
    { email, password, backendBaseUrl },
    'Anmeldung fehlgeschlagen',
  );
  csrfToken = result.csrfToken;
  // Persist what the server normalised, not the raw typing — so the
  // field shows the same canonical form the session actually uses.
  storeBackendBaseUrl(result.backendBaseUrl);
}

export async function logout(): Promise<void> {
  try {
    await request<void>('POST', '/session/logout');
  } finally {
    // Whatever the server said, this tab is done with the session.
    csrfToken = null;
  }
}

// -- Parent API (mirror of Android's `ParentApi`) ---------------------------

export const parentApi = {
  listChildProfiles: () =>
    request<ChildProfile[]>('GET', '/api/child-profiles', undefined, 'Kinderprofile konnten nicht geladen werden'),

  createChildProfile: (displayName: string) =>
    request<ChildProfile>('POST', '/api/child-profiles', { displayName }, 'Kinderprofil konnte nicht erstellt werden'),

  /** Irreversible — everything cascaded from the child goes with it. */
  deleteChildProfile: (childProfileId: string) =>
    request<void>('DELETE', `/api/child-profiles/${childProfileId}`, undefined, 'Kind konnte nicht gelöscht werden'),

  listDevices: (childProfileId: string) =>
    request<ChildDevice[]>('GET', `/api/child-profiles/${childProfileId}/devices`),

  revokeDevice: (childDeviceId: string) =>
    request<void>('DELETE', `/api/child-devices/${childDeviceId}`, undefined, 'Gerät konnte nicht entkoppelt werden'),

  createPairingCode: (childProfileId: string) =>
    request<PairingCode>(
      'POST',
      `/api/child-profiles/${childProfileId}/pairing-codes`,
      {},
      'Pairing-Code konnte nicht erstellt werden',
    ),

  cancelPairingCode: (childProfileId: string, pairingCodeId: string) =>
    request<void>(
      'DELETE',
      `/api/child-profiles/${childProfileId}/pairing-codes/${pairingCodeId}`,
      undefined,
      'Pairing-Code konnte nicht abgebrochen werden',
    ),

  getInternetAccessAccount: (childProfileId: string) =>
    request<InternetAccessAccount>('GET', `/api/child-profiles/${childProfileId}/internet-access`),

  /** Adds to the bonus pot, in 5-minute steps (enforced server-side too). */
  topUpInternetAccess: (childProfileId: string, minutes: number) =>
    request<InternetAccessAccount>(
      'POST',
      `/api/child-profiles/${childProfileId}/internet-access/top-up`,
      { minutes },
      'Guthaben konnte nicht aufgeladen werden',
    ),

  /** Mirror of the top-up — removes from the bonus pot only, clamped at zero. */
  deductBonusInternetAccess: (childProfileId: string, minutes: number) =>
    request<InternetAccessAccount>(
      'POST',
      `/api/child-profiles/${childProfileId}/internet-access/deduct-bonus`,
      { minutes },
      'Bonus konnte nicht abgezogen werden',
    ),

  /** Override, e.g. "Hausaufgaben" — cuts off internet regardless of balance. */
  setForceBlocked: (childProfileId: string, forceBlocked: boolean) =>
    request<InternetAccessAccount>(
      'PUT',
      `/api/child-profiles/${childProfileId}/internet-access/force-blocked`,
      { forceBlocked },
      'Sperre konnte nicht gespeichert werden',
    ),

  /** Adds a fixed daily Sperrzeit — enforced by the backend's clock, not the child's. */
  addBlockWindow: (childProfileId: string, startTime: string, endTime: string) =>
    request<InternetAccessAccount>(
      'POST',
      `/api/child-profiles/${childProfileId}/internet-access/block-windows`,
      { startTime, endTime },
      'Sperrzeit konnte nicht gespeichert werden',
    ),

  removeBlockWindow: (childProfileId: string, blockWindowId: string) =>
    request<InternetAccessAccount>(
      'DELETE',
      `/api/child-profiles/${childProfileId}/internet-access/block-windows/${blockWindowId}`,
      undefined,
      'Sperrzeit konnte nicht entfernt werden',
    ),

  /** Hard reset of the daily pot — discards leftover/overdrawn time. */
  resetInternetAccess: (childProfileId: string) =>
    request<InternetAccessAccount>(
      'POST',
      `/api/child-profiles/${childProfileId}/internet-access/reset`,
      {},
      'Guthaben konnte nicht zurückgesetzt werden',
    ),
};
